import React, { useState, useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    DateField,
    TextInput,
    FilterButton,
    ExportButton,
    TopToolbar,
    Show,
    SimpleShowLayout,
    useShowController,
    useRecordContext,
    BulkDeleteButton,
    useRedirect,
    useDataProvider,
    useGetOne,
    FunctionField,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ZoomIn as ZoomInIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

const BackButton = () => {
    const redirect = useRedirect();
    return (
        <Button
            onClick={() => redirect('/aiusage')}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
                mr: 2,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                    backgroundColor: 'primary.light',
                    borderColor: 'primary.dark',
                },
            }}
        >
            Volver a Lista
        </Button>
    );
};

const ImageField = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [imgError, setImgError] = useState(false);

    if (!record || !record.imageurl) return <span>-</span>;

    const handleOpen = (e) => {
        e.stopPropagation();
        setOpen(true);
        setImgError(false);
    };

    const handleClose = (e) => {
        e?.stopPropagation();
        setOpen(false);
    };

    return (
        <>
            <Tooltip title="Ver imagen">
                <IconButton size="small" onClick={handleOpen}>
                    <ZoomInIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                onClick={(e) => e.stopPropagation()}
                PaperProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.9)' } }}
            >
                <DialogActions sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogActions>
                <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    {!imgError ? (
                        <img
                            src={record.imageurl}
                            alt="AI usage"
                            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                            onError={() => setImgError(true)}
                        />
                    ) : (
                        <Box sx={{ color: 'white', textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>No se pudo cargar la imagen</Typography>
                            <Button variant="contained" onClick={() => window.open(record.imageurl, '_blank')}>
                                Abrir en nueva pestaña
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

const JsonResultField = ({ value }) => {
    let parsed = null;
    let isJson = false;

    try {
        parsed = JSON.parse(value);
        isJson = true;
    } catch {
        isJson = false;
    }

    if (!isJson) {
        return (
            <pre style={{ margin: 0, fontSize: '0.875rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {value}
            </pre>
        );
    }

    const formatted = JSON.stringify(parsed, null, 2);

    const colorize = (json) => {
        return json.replace(
            /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
            (match) => {
                let color = '#e06c75';
                if (/^"/.test(match)) {
                    color = /:$/.test(match) ? '#61afef' : '#98c379';
                } else if (/true|false/.test(match)) {
                    color = '#d19a66';
                } else if (/null/.test(match)) {
                    color = '#c678dd';
                }
                return `<span style="color:${color}">${match}</span>`;
            }
        );
    };

    return (
        <pre
            style={{ margin: 0, fontSize: '0.875rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#abb2bf' }}
            dangerouslySetInnerHTML={{ __html: colorize(formatted) }}
        />
    );
};

const TruncatedTextField = ({ source, maxLength = 80, label }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return <span>-</span>;
    const value = String(record[source]);
    return (
        <Tooltip title={value}>
            <span style={{ cursor: 'default' }}>
                {value.length > maxLength ? value.substring(0, maxLength) + '...' : value}
            </span>
        </Tooltip>
    );
};

const UserNameLinkField = () => {
    const record = useRecordContext();
    const { data: user, isLoading } = useGetOne(
        'users',
        { id: record?.iduser },
        { enabled: !!record?.iduser }
    );

    if (!record?.iduser) return <span>-</span>;
    if (isLoading) return <CircularProgress size={14} />;

    const displayName = user?.username || user?.email || '-';

    return (
        <Link
            to={`/users/${record.iduser}/show`}
            style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
            onClick={(e) => e.stopPropagation()}
        >
            {displayName}
        </Link>
    );
};

const MonthlyTokensChart = () => {
    const dataProvider = useDataProvider();
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        dataProvider
            .getList('aiusage', {
                pagination: { page: 1, perPage: 2000 },
                sort: { field: 'date', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => {
                const monthMap = {};
                data.forEach((record) => {
                    if (!record.date) return;
                    const d = new Date(record.date);
                    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    if (!monthMap[key]) {
                        monthMap[key] = { month: key, input_tokens: 0, output_tokens: 0, total: 0 };
                    }
                    monthMap[key].input_tokens += record.input_tokens || 0;
                    monthMap[key].output_tokens += record.output_tokens || 0;
                    monthMap[key].total += (record.input_tokens || 0) + (record.output_tokens || 0);
                });
                const sorted = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
                setChartData(sorted);
            })
            .catch(() => setChartData([]))
            .finally(() => setLoading(false));
    }, [dataProvider]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!chartData.length) return null;

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    📊 Consumo Mensual de Tokens
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                        <RechartsTooltip
                            formatter={(value, name) => [value.toLocaleString(), name === 'input_tokens' ? 'Tokens Entrada' : name === 'output_tokens' ? 'Tokens Salida' : 'Total']}
                            labelFormatter={(label) => `Mes: ${label}`}
                        />
                        <Legend formatter={(value) => value === 'input_tokens' ? 'Tokens Entrada' : value === 'output_tokens' ? 'Tokens Salida' : 'Total'} />
                        <Bar dataKey="input_tokens" stackId="a" fill="#1976d2" />
                        <Bar dataKey="output_tokens" stackId="a" fill="#42a5f5" />
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

const AiUsageFilters = [
    <TextInput source="q" label="Buscar" alwaysOn />,
    <TextInput source="iduser" label="ID Usuario" />,
    <TextInput source="idproyectos" label="ID Proyecto" />,
    <TextInput source="opts" label="Opts" />,
    <TextInput source="description" label="Descripción" />,
];

const AiUsageListActions = () => (
    <TopToolbar>
        <FilterButton />
        <ExportButton />
    </TopToolbar>
);

export const AiUsageList = () => (
    <>
        <MonthlyTokensChart />
        <List
            filters={AiUsageFilters}
            actions={<AiUsageListActions />}
            perPage={50}
            sort={{ field: 'date', order: 'DESC' }}
            filterDefaultValues={{}}
        >
            <Datagrid bulkActionButtons={<BulkDeleteButton />} rowClick="show">
                <TextField source="id" />
                <TextField source="iduser" label="ID Usuario" />
                <FunctionField label="Usuario" render={() => <UserNameLinkField />} />
                <TextField source="idproyectos" label="ID Proyecto" />
                <TruncatedTextField source="opts" label="Opts" maxLength={50} />
                <TruncatedTextField source="description" label="Descripción" maxLength={60} />
                <TruncatedTextField source="result" label="Resultado" maxLength={60} />
                <NumberField source="input_tokens" label="Tokens Entrada" />
                <NumberField source="output_tokens" label="Tokens Salida" />
                <NumberField source="raw_input_tokens" label="Tokens Raw" />
                <DateField source="date" label="Fecha" showTime />
                <ImageField label="Imagen" />
            </Datagrid>
        </List>
    </>
);

export const AiUsageShow = () => {
    const { record, isLoading, error } = useShowController();

    if (isLoading) {
        return (
            <Show>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>Cargando datos...</Typography>
                </Box>
            </Show>
        );
    }

    if (error) {
        return (
            <Show>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'error.main' }}>
                        Error al cargar los datos: {error.message}
                    </Typography>
                </Box>
            </Show>
        );
    }

    if (!record) {
        return (
            <Show>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'warning.main' }}>
                        No se encontraron datos
                    </Typography>
                </Box>
            </Show>
        );
    }

    return (
        <Show actions={<TopToolbar><BackButton /></TopToolbar>}>
            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 1 }}>
                                    🤖 Información General
                                </Typography>
                                <SimpleShowLayout>
                                    <TextField source="id" label="ID" />
                                    <TextField source="iduser" label="ID Usuario" />
                                    <TextField source="idproyectos" label="ID Proyecto" />
                                    <TextField source="opts" label="Opciones" />
                                    <DateField source="date" label="Fecha" showTime />
                                </SimpleShowLayout>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main', borderBottom: '2px solid', borderColor: 'secondary.main', pb: 1 }}>
                                    📊 Tokens Utilizados
                                </Typography>
                                <SimpleShowLayout>
                                    <NumberField source="input_tokens" label="Tokens de Entrada" />
                                    <NumberField source="output_tokens" label="Tokens de Salida" />
                                    <NumberField source="raw_input_tokens" label="Tokens Raw de Entrada" />
                                </SimpleShowLayout>
                                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Total tokens: <strong>{(record.input_tokens || 0) + (record.output_tokens || 0)}</strong>
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {record.description && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2, color: 'success.main', borderBottom: '2px solid', borderColor: 'success.main', pb: 1 }}>
                                        📝 Descripción
                                    </Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {record.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {record.result && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2, color: 'info.main', borderBottom: '2px solid', borderColor: 'info.main', pb: 1 }}>
                                        💡 Resultado
                                    </Typography>
                                    <Box sx={{ backgroundColor: '#282c34', p: 2, borderRadius: 1, border: '1px solid #3e4451', maxHeight: '500px', overflow: 'auto' }}>
                                        <JsonResultField value={record.result} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {record.imageurl && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2, color: 'warning.main', borderBottom: '2px solid', borderColor: 'warning.main', pb: 1 }}>
                                        🖼️ Imagen
                                    </Typography>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <img
                                            src={record.imageurl}
                                            alt="AI usage"
                                            style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', borderRadius: '8px' }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        <Box sx={{ mt: 1 }}>
                                            <Button variant="outlined" size="small" onClick={() => window.open(record.imageurl, '_blank')}>
                                                Abrir en nueva pestaña
                                            </Button>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Show>
    );
};